<template>
    <div class="company-display-settings-content">
        <div class="content-header">
            <h5 id="company-display-modal-title" class="company-display-settings-title">求人表示する企業設定</h5>
            <button
                type="button"
                class="content-close"
                aria-label="閉じる"
                data-testid="company-display-settings-close"
                @click="$emit('close')"
            >
                <span aria-hidden="true">&times;</span>
            </button>
        </div>

        <div class="content-body">
            <div class="company-display-settings-body">
                <div class="company-display-settings-buttons d-flex align-items-center mb-3">
                    <div class="btn-group-segment">
                        <button
                            type="button"
                            class="btn btn-registered-only"
                            :class="{ 'segment-btn-active': quickFilter === 'registered' }"
                            data-testid="filter-registered-only"
                            @click="applyRegisteredOnly"
                        >
                            登録企業のみ
                        </button>
                        <button
                            type="button"
                            class="btn btn-select-all"
                            :class="{ 'segment-btn-active': quickFilter === 'all' }"
                            data-testid="filter-select-all"
                            @click="applySelectAll"
                        >
                            すべて選択
                        </button>
                    </div>
                    <button
                        type="button"
                        class="btn btn-clear"
                        data-testid="filter-clear"
                        @click="applyClear"
                    >
                        クリア
                    </button>
                </div>

                <div class="company-list-wrapper">
                    <h6 class="company-list-heading">Bloomキャリア登録導入企業一覧</h6>
                    <p class="company-list-note">
                        ※現在、一部の導入企業限定で活用できる機能です<br class="mobile-break" />
                        <span class="note-indent">対象企業は順次追加されます</span>
                    </p>
                    <div class="company-list-scroll" data-testid="company-list-scroll">
                        <div
                            v-for="company in displayCompanies"
                            :key="company._id"
                            class="company-list-item"
                            :class="{ disabled: !isCompanySelectable(company) }"
                        >
                            <input
                                :id="`company-cb-${company._id}`"
                                type="checkbox"
                                class="company-checkbox"
                                :checked="isCompanyChecked(company._id)"
                                :disabled="!isCompanySelectable(company)"
                                data-testid="company-checkbox"
                                @change="toggleCompany(company._id)"
                            />
                            <label :for="`company-cb-${company._id}`" class="company-name-label">
                                {{ company.name || company.companyName || '' }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-footer">
            <button
                type="button"
                class="btn-apply-settings"
                data-testid="company-display-apply"
                @click="handleApply"
            >
                設定する
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CompanyDisplaySettingsContent',
    props: {
        companies: {
            type: Array,
            default: () => [],
        },
        selectedCompanyIds: {
            type: Array,
            default: () => [],
        },
        registeredCompanyIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            localSelectedIds: [],
            quickFilter: 'registered',
        };
    },
    computed: {
        displayCompanies() {
            return this.companies || [];
        },
        registeredSelectableIds() {
            const registered = this.registeredCompanyIds || [];
            return registered.filter((id) => {
                const company = this.displayCompanies.find(c => String(c._id) === id);
                return company && this.isCompanySelectable(company);
            });
        },
        selectedSelectableIds() {
            return this.localSelectedIds.filter((id) => {
                const company = this.displayCompanies.find(c => String(c._id) === id);
                return company && this.isCompanySelectable(company);
            });
        },
    },
    watch: {
        selectedCompanyIds: {
            immediate: true,
            handler(ids) {
                this.localSelectedIds = Array.isArray(ids) ? [...ids] : [];
            },
        },
        localSelectedIds: {
            handler(newIds) {
                const selected = [...this.selectedSelectableIds].map(String).sort();
                const registered = [...this.registeredSelectableIds].map(String).sort();
                const allSelectable = this.displayCompanies
                    .filter(c => this.isCompanySelectable(c))
                    .map(c => String(c._id))
                    .sort();
                
                // Only update quickFilter if there's an exact match
                if (this.arraysEqual(selected, allSelectable)) {
                    this.quickFilter = 'all';
                } else if (this.arraysEqual(selected, registered)) {
                    this.quickFilter = 'registered';
                }
                // If it's a custom selection, don't change quickFilter
            },
            deep: true,
        },
    },
    methods: {
        isCompanySelectable(company) {
            if (!company) return false;
            return company.scout_profile_enabled === true;
        },
        isCompanyChecked(companyId) {
            return this.localSelectedIds.includes(String(companyId));
        },
        toggleCompany(companyId) {
            if (!this.isCompanySelectable(this.displayCompanies.find(c => c._id === companyId))) return;
            const id = String(companyId);
            const idx = this.localSelectedIds.indexOf(id);
            if (idx === -1) {
                this.localSelectedIds = [...this.localSelectedIds, id];
            } else {
                this.localSelectedIds = this.localSelectedIds.filter(i => i !== id);
            }
            // Force reactivity
            this.localSelectedIds = [...this.localSelectedIds];
        },
        applyRegisteredOnly() {
            this.localSelectedIds = [...(this.registeredSelectableIds || [])];
            this.quickFilter = 'registered';
            // Force reactivity
            this.$forceUpdate();
        },
        applySelectAll() {
            const selectable = this.displayCompanies
                .filter(c => this.isCompanySelectable(c))
                .map(c => String(c._id));
            this.localSelectedIds = [...selectable];
            this.quickFilter = 'all';
            // Force reactivity
            this.$forceUpdate();
        },
        applyClear() {
            this.localSelectedIds = [];
            this.quickFilter = 'registered';
            // Force reactivity
            this.$forceUpdate();
        },
        handleApply() {
            const enabledIds = this.localSelectedIds.filter(id => {
                const company = this.displayCompanies.find(c => String(c._id) === id);
                return company && company.scout_profile_enabled === true;
            });
            this.$emit('apply', [...enabledIds]);
            this.$emit('close');
        },
        arraysEqual(a, b) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        },
    },
};
</script>

<style scoped>
.company-display-settings-content {
    display: flex;
    flex-direction: column;
    background-color: #E5E7EB;
    height: 100%;
    min-height: 0;
}

.content-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    background-color: #E5E7EB;
    border-bottom: none;
    flex-shrink: 0;
    gap: 8px;
}

.company-display-settings-title {
    font-weight: 800;
    font-size: 18px;
    color: #3c3c3c;
    margin: 0;
}

.content-close {
    background: none;
    border: none;
    font-size: 28px;
    line-height: 1;
    color: #3c3c3c;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.content-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background-color: #E5E7EB;
}

.company-display-settings-body {
    padding: 0 1.25rem 1rem;
    overflow: hidden;
}

.company-display-settings-buttons {
    gap: 0;
}


.btn-group-segment {
    display: inline-flex;
    border: 1px solid #ccc;
    border-radius: 18px;
    overflow: hidden;
}

.btn-group-segment .btn {
    margin: 0;
    border: none;
    border-radius: 0;
    padding: 4px 10px;
    font-size: 16px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    color: #999;
    font-weight: normal;
}

.btn-group-segment .btn:not(:last-child) {
    border-right: 1px solid #ccc;
}

.btn-group-segment .btn-registered-only.segment-btn-active,
.btn-group-segment .btn-select-all.segment-btn-active {
    background-color: #E7F6F6 !important;
    color: #006C6B !important;
    font-weight: bold;
}

.btn-clear {
    margin-left: 12px;
    padding: 0 20px;
    height: 50px;
    font-size: 20px;
    font-weight: 700;
    background-color: #666;
    border: 1px solid #666;
    border-radius: 4px;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.company-list-wrapper {
    background-color: #fff;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 0;
}

.company-list-heading {
    font-size: 16px;
    font-weight: bold;
    color: #3c3c3c;
    margin-bottom: 6px;
    text-align: center;
}

.company-list-note {
    font-size: 12px;
    font-weight: normal;
    color: #3c3c3c;
    margin-bottom: 12px;
    line-height: 1.5;
    text-align: center;
}

.company-list-scroll {
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px;
}

.company-list-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 8px;
    border-bottom: 1px solid #f0f0f0;
    gap: 0;
}

.company-list-item:last-child {
    border-bottom: none;
}

.company-list-item.disabled {
    opacity: 0.6;
}

.company-checkbox {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    cursor: pointer;
    margin-left: 6px;
}

.company-list-item.disabled .company-checkbox {
    cursor: not-allowed;
}

.company-name-label {
    flex: 1;
    min-width: 0;
    margin: 0 0 0 4px;
    font-size: 14px;
    color: #3c3c3c;
    cursor: pointer;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
}

.company-list-item.disabled .company-name-label {
    cursor: not-allowed;
}

/* Arrow icon removed - no longer used */

.content-footer {
    padding: 1rem 1.25rem;
    background-color: #E5E7EB;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: center;
    flex-shrink: 0;
}

.btn-apply-settings {
    width: 150px;
    height: 40px;
    padding: 0 25px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #009D9E;
    border: 1px solid #009D9E;
    border-radius: 4px;
    color: #fff;
    font-weight: bold;
    font-size: 16px;
    cursor: pointer;
}

.btn-apply-settings:hover {
    background-color: #008a8b;
    border-color: #008a8b;
    color: #fff;
}
@media (min-width: 768px) {
    .content-header {
        justify-content: center;
        position: relative;
    }

    .content-close {
        position: absolute;
        right: 1.25rem;
    }

    .company-display-settings-buttons {
        justify-content: center;
    }

    .btn-clear {
        width: 152px;
        height: 40px;
        padding: 0;
        font-size: 18px;
        margin-left: 48px;
    }

    .company-list-scroll {
        display: flex;
        flex-wrap: wrap;
        max-height: 400px;
        padding: 15px;
        border: none;
    }

    .company-list-item {
        width: 50%;
        border-bottom: none;
        padding: 8px 10px 8px 0;
        display: flex;
        align-items: flex-start;
        gap: 0;
    }

    /* Custom Scrollbar Styles */
    .company-list-scroll::-webkit-scrollbar {
        width: 12px;
    }

    .company-list-scroll::-webkit-scrollbar-track {
        background: transparent;
    }

    .company-list-scroll::-webkit-scrollbar-thumb {
        background-color: #999;
        border-radius: 10px;
        border: 2px solid #fff;
    }

    .company-list-scroll {
        scrollbar-width: medium;
        scrollbar-color: #999 transparent;
    }
}

.company-list-select-all-container {
}

/* Mobile responsive styles */
@media (max-width: 767px) {
    .content-header {
        justify-content: center;
        position: relative;
        padding: 1rem;
    }

    .company-display-settings-title {
        flex: 1;
        text-align: center;
        font-size: 16px;
    }

    .content-close {
        position: absolute;
        right: 1.25rem;
    }

    .company-list-note {
        font-size: 10px;
    }

    .mobile-break {
        display: block;
    }

    .note-indent {
        display: inline-block;
        text-indent: 0.5em;
    }
}
</style>
